import { Box, createTheme, ThemeProvider } from '@mui/material'
import {
  DataGrid,
  enUS,
  type GridCellParams,
  type GridColDef,
  type GridComparatorFn,
  type GridValueGetterParams,
} from '@mui/x-data-grid'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type * as React from 'react'
import { useContext, useState } from 'react'
import { DeleteButton } from '../components/DeleteButton'
import { useAuth } from '../hooks/useAuth'
import type { ConversationLogIf } from '../interfaces/ConversationLogIf'
import { LanguageContext } from '../util/LanguageContext'

// interface ConversationContentTimeCellValue {
//   conversation_content: string;
//   time: Date;
// }

// interface ModifyContentTimeCellValue {
//   modify_content: string;
//   time: Date;
// }

const conversationContentTimeSortComparator: GridComparatorFn = (v1, v2, param1, param2) => {
  const row1 = param1.api.getRow(param1.id)
  const row2 = param2.api.getRow(param2.id)
  const v1c = v1.conversation_content === null ? '' : v1.conversation_content
  const v2c = v2.conversation_content === null ? '' : v2.conversation_content
  if (row1 && row1.time && row2 && row2.time) {
    const vCompare = v1c.localeCompare(v2c)
    // 対象カラムでソートしてからtimeでソートする
    return vCompare || new Date(row1.time).getTime() - new Date(row2.time).getTime()
  }
  return 0
}

const modifyContentTimeSortComparator: GridComparatorFn = (v1, v2, param1, param2) => {
  const row1 = param1.api.getRow(param1.id)
  const row2 = param2.api.getRow(param2.id)
  const v1c = v1.modify_content === null ? '' : v1.modify_content
  const v2c = v2.modify_content === null ? '' : v2.modify_content
  if (row1 && row1.time && row2 && row2.time) {
    const vCompare = v1c.localeCompare(v2c)
    // 対象カラムでソートしてからtimeでソートする
    return vCompare || new Date(row1.time).getTime() - new Date(row2.time).getTime()
  }
  return 0
}

type ConversationLogListProps = {
  items: ConversationLogIf[]
  onEdit: (convId: string, orgText: boolean) => void
  onDownload: (convId: string) => void
  onDelete: (convId: string) => void
}

export const ConversationLogList: React.FC<ConversationLogListProps> = ({
  items,
  onEdit,
  onDownload,
  onDelete,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedField, setSelectedField] = useState<string | null>(null)
  const [opacity, setOpacity] = useState(null)
  const auth = useAuth()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  const handleMouseEnter = (id: string, field: string) => {
    setSelectedId(id)
    setSelectedField(field)
  }

  const handleMouseLeave = () => {
    setSelectedId(null)
    setSelectedField(null)
  }

  const handleDownloadClick = (conv_id: string) => {
    onDownload(conv_id)
  }

  const handleDeleteClick = (conv_id: string) => {
    onDelete(conv_id)
  }

  const getDownloadImage = (language: string) => {
    switch (language) {
      case 'de':
        return require('../images/Btn Download_de.svg').ReactComponent
      case 'en':
        return require('../images/Btn Download_en.svg').ReactComponent
      case 'es':
        return require('../images/Btn Download_es.svg').ReactComponent
      case 'it':
        return require('../images/Btn Download_it.svg').ReactComponent
      case 'ja':
        return require('../images/Btn Download_ja.svg').ReactComponent
      case 'nl':
        return require('../images/Btn Download_nl.svg').ReactComponent
      case 'sv':
        return require('../images/Btn Download_sv.svg').ReactComponent
      case 'tr':
        return require('../images/Btn Download_tr.svg').ReactComponent
      default:
        return require('../images/Btn Download.svg').ReactComponent // デフォルトは英語
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'conversation_id',
      headerName: translation['label_conversation_ID'],
      editable: false,
      flex: 4,
      renderCell: (params: GridCellParams) => {
        let conversationIdTime: string = params.row.time
        conversationIdTime = conversationIdTime.replaceAll('/', '')
        conversationIdTime = conversationIdTime.replaceAll(' ', '')
        conversationIdTime = conversationIdTime.replaceAll(':', '')
        return (
          <span style={{ display: 'block' }}>
            {auth.deviceId}_{conversationIdTime}
          </span>
        )
      },
      sortable: false,
    },
    {
      field: 'time',
      headerName: translation['label_time'],
      editable: false,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        const value = params.value as React.ReactNode
        return <span style={{ display: 'block' }}>{value}</span>
      },
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'conversation_content',
      headerName: translation['label_conversation_content'],
      editable: false,
      flex: 4,

      renderCell: (params: GridCellParams) => {
        const value = params.row.conversation_content
        return (
          <div // CustomCursorCell コンポーネントのスタイルをここに直接指定
            style={{
              cursor: params.field === 'conversation_content' ? 'pointer' : 'default',
              display: 'block',
              textDecoration:
                params.row.id === selectedId && selectedField === 'conversation_content'
                  ? 'underline'
                  : 'none',
            }}
            onMouseEnter={() => handleMouseEnter(params.row.id, params.field)}
            onMouseLeave={handleMouseLeave}
          >
            {value}
          </div>
        )
      },
      valueGetter: (params: GridValueGetterParams) => ({
        id: params.row.id,
        conversation_content: params.row.conversation_content,
        time: params.row.time,
      }),
      sortComparator: conversationContentTimeSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'modify_content',
      headerName: translation['label_edited_content'],
      editable: false,
      flex: 4,
      renderCell: (params: GridCellParams) => {
        var value = ''
        if (params.row.modify_illustration_num === 0 && params.row.modify_content === '') {
          value = translation['already_edited_blank']
          return (
            <div // CustomCursorCell コンポーネントのスタイルをここに直接指定
              style={{
                cursor: 'default',
                display: 'block',
                textDecoration: 'none',
              }}
              onMouseLeave={handleMouseLeave}
            >
              {value}
            </div>
          )
        }
        if (params.row.modify_illustration_num !== 0 && params.row.modify_content === '') {
          value = translation['already_edited_images_only']
          return (
            <div // CustomCursorCell コンポーネントのスタイルをここに直接指定
              style={{
                cursor: params.field === 'modify_content' ? 'pointer' : 'default',
                display: 'block',
                textDecoration:
                  params.row.id === selectedId && selectedField === 'modify_content'
                    ? 'underline'
                    : 'none',
              }}
              onMouseEnter={() => handleMouseEnter(params.row.id, params.field)}
              onMouseLeave={handleMouseLeave}
            >
              {value}
            </div>
          )
        }
        value = params.row.modify_content
        return (
          <div // CustomCursorCell コンポーネントのスタイルをここに直接指定
            style={{
              cursor: params.field === 'modify_content' ? 'pointer' : 'default',
              display: 'block',
              textDecoration:
                params.row.id === selectedId && selectedField === 'modify_content'
                  ? 'underline'
                  : 'none',
            }}
            onMouseEnter={() => handleMouseEnter(params.row.id, params.field)}
            onMouseLeave={handleMouseLeave}
          >
            {value}
          </div>
        )
      },
      valueGetter: (params: GridValueGetterParams) => ({
        modify_content: params.row.modify_content,
        time: params.row.time,
      }),
      sortComparator: modifyContentTimeSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'download',
      headerName: '',
      editable: false,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        const userLanguage = navigator.language.split('-')[0] // "en", "ja", "es"など
        const DownloadImage = getDownloadImage(userLanguage)
        return (
          <Box style={{ margin: 'auto' }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <DownloadImage
              width="102px"
              height="32px"
              onClick={() => handleDownloadClick(params.row.conv_id)}
              onMouseOver={() => setOpacity(params.row.id)}
              onMouseOut={() => setOpacity(null)}
              style={{ opacity: opacity === params.row.id ? 0.8 : 1 }}
            />
          </Box>
        )
      },
      sortable: false,
    },
    {
      field: 'delete',
      headerName: '',
      editable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return (
          <Box style={{ margin: 'auto' }} sx={{ display: 'flex', justifyContent: 'center' }}>
            <DeleteButton
              id={params.row.conv_id}
              text={params.row.time}
              onDel={handleDeleteClick}
            />
          </Box>
        )
      },
      sortable: false,
    },
  ]
  const handleCellClick = (params: GridCellParams) => {
    const clickedColumn = params.field
    const value = params.value

    if (value !== '') {
      if (clickedColumn === 'conversation_content') {
        // 「会話内容」のカラムがクリックされた場合の処理
        onEdit(params.row.conv_id, true)
      }
      if (clickedColumn === 'modify_content') {
        if (
          params.row.modify_content !== null &&
          !(params.row.modify_illustration_num === 0 && params.row.modify_content === '')
        ) {
          // 「編集済みの内容」のカラムがクリックされた場合の処理
          onEdit(params.row.conv_id, false)
        }
      }
    }
  }
  const theme = createTheme(
    {
      components: {
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: 'none',
              backgroundColor: 'white',
            },
            columnHeader: {
              borderBottom: '2px solid #000',
              '&:focus-within': {
                outline: 'none', // クリック時のヘッダー部の青枠を非表示
              },
            },
            columnHeaderTitle: {
              fontWeight: 'bold',
            },
            cell: {
              '&:focus-within': {
                outline: 'none', // クリック時のデータ部の青枠を非表示
              },
            },
          },
        },
      },
    },
    enUS,
  )

  return (
    <div style={{ height: 'calc(100vh - 372px)', width: '100%' }}>
      <ThemeProvider theme={theme}>
        <DataGrid
          rows={items}
          columns={columns}
          disableRowSelectionOnClick={auth.isDisable}
          onCellClick={handleCellClick}
          sx={{
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'visible',
            },
            '& .muidatagrid-root': {
              border: 'none',
            },
          }}
          style={{ font: 'normal normal normal 21px/32px Noto Sans JP' }}
          // hideFooter
          hideFooterSelectedRowCount
          disableColumnMenu
          localeText={{
            ...enUS.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: '',
          }}
        />
      </ThemeProvider>
    </div>
  )
}
